// Estado falso de Pessoas (modo fake, só em dev — Parte 0.6; GIY-067): pessoas de rede com cofre (identificador fiscal só em
// claro aqui, no "cofre" do estado falso; a ficha devolve só a máscara), vínculos com a holding e com os centros, funções e
// Matriz de Enquadramento v1 (Anexo E.3), licenças, certificações, capacidades, escala, disponibilidade, ausências,
// onboarding/offboarding, exceções de transição, lotes de importação e as aprovações de duas chaves da Matriz e da exceção.
// Semeado por cenário de `/me`: um centro (PILOT) com pessoas de vínculos mistos e a própria pessoa da claim `pid`.
import type { MeResponse, components } from '@giyus/api-client';

import { FIXTURE_IDS, FIXTURE_PEOPLE } from '../auth/me.fixture';
import { HOLDING_LEGAL_ENTITY_ID, addDays, formatCnpj } from './tenancyStore';

type Schemas = components['schemas'];

export type PersonContactResponse = Schemas['PeoplePersonContactResponse'];
export type PersonLinkResponse = Schemas['PeoplePersonLinkResponse'];
export type PersonDetailResponse = Schemas['PeoplePersonDetailResponse'];
export type PersonSummaryResponse = Schemas['PeoplePersonSummaryResponse'];
export type FunctionResponse = Schemas['PeopleFunctionResponse'];
export type MatrixVersionResponse = Schemas['PeopleMatrixVersionResponse'];
export type MatrixVersionSummaryResponse = Schemas['PeopleMatrixVersionSummaryResponse'];
export type MatrixEntryResponse = Schemas['PeopleMatrixEntryResponse'];
export type CertificationTypeResponse = Schemas['PeopleCertificationTypeResponse'];
export type BoardResponse = Schemas['PeopleProfessionalRegistrationBoardResponse'];
export type SkillCatalogResponse = Schemas['PeopleSkillCatalogEntryResponse'];
export type LicenseResponse = Schemas['PeopleLicenseResponse'];
export type CertificationResponse = Schemas['PeopleCertificationResponse'];
export type PersonSkillResponse = Schemas['PeoplePersonSkillResponse'];
export type WorkScheduleResponse = Schemas['PeopleWorkScheduleResponse'];
export type AvailabilityResponse = Schemas['PeopleAvailabilityResponse'];
export type AbsenceResponse = Schemas['PeopleAbsenceResponse'];
export type ChecklistItemResponse = Schemas['PeopleChecklistItemResponse'];
export type OnboardingCaseResponse = Schemas['PeopleOnboardingCaseResponse'];
export type OffboardingCaseResponse = Schemas['PeopleOffboardingCaseResponse'];
export type TransitionExceptionResponse = Schemas['PeopleTransitionExceptionResponse'];
export type ImportBatchResponse = Schemas['PeopleImportBatchResponse'];
export type ImportRowResponse = Schemas['PeopleImportRowResponse'];
export type TwoKeyApprovalResponse = Schemas['AuthorizationTwoKeyApprovalResponse'];

export type VisibilityReason = 'Link' | 'Assignment' | 'Delegation';
export type FakeLinkType = 'Socio' | 'CLT' | 'PJ' | 'CooperadoCPESQ' | 'Estagiario';
export type FakeIndication = 'Indicated' | 'CaseByCase' | 'NotAllowed';

/** Pessoa de rede: sem `TenantId`; cada tenant só a vê com uma razão de visibilidade ativa (REQ-PPL-015). */
export interface FakePerson {
  readonly id: string;
  givenName: string;
  familyName: string;
  socialName: string | null;
  country: string;
  /** Cofre: identificador fiscal em claro (só a leitura sensível com propósito o devolve). `null` = pessoa pendente. */
  federalCode: string | null;
  birthDate: string | null;
  preferredLocale: string;
  photoDocumentId: string | null;
  status: 'Active' | 'Inactive';
  contacts: PersonContactResponse[];
  readonly createdAt: string;
  modifiedAt: string | null;
  version: number;
  /** `tenantId` → razões de visibilidade ativas. */
  visibility: Record<string, VisibilityReason[]>;
}

export interface FakeLink extends PersonLinkResponse {
  version: number;
}

export interface FakeLicense extends LicenseResponse {
  version: number;
}

export interface FakeCertification extends CertificationResponse {
  version: number;
}

export interface FakeSchedule extends WorkScheduleResponse {
  version: number;
}

export interface FakeAvailability extends AvailabilityResponse {
  version: number;
}

export interface FakeAbsence extends AbsenceResponse {
  removed: boolean;
}

export interface FakeException extends TransitionExceptionResponse {
  version: number;
}

export interface FakeMatrixVersion extends MatrixVersionResponse {
  /** Número da versão da Matriz (campo do contrato). */
  version: number;
  /** Contador de linha: base do ETag (`If-Match`), distinto do número da versão da Matriz. */
  rowVersion: number;
}

/** Aprovação de duas chaves das ações de pessoas (a mesma rota genérica `/authz/two-key/{id}/second-key`). */
export interface FakeApproval extends TwoKeyApprovalResponse {
  readonly firstActorRoles: readonly string[];
}

export interface FakeImportBatch extends ImportBatchResponse {
  readonly tenantId: string;
  readonly fingerprint: string;
  readonly rowReport: ImportRowResponse[];
}

export interface FakePeopleTenant {
  readonly id: string;
  readonly code: string;
  readonly legalName: string;
  readonly legalEntityId: string;
  readonly siteIds: readonly string[];
  /** Códigos de site (coluna `primarySiteCode` do CSV de importação) → id. */
  readonly siteCodes: Readonly<Record<string, string>>;
}

export interface PeopleSettings {
  readonly minIntervalDays: number;
  readonly internMaxDailyHours: number;
  readonly alertDays: readonly number[];
  readonly importMaxRows: number;
  readonly importMaxDuplicateRows: number;
  readonly importMaxDuplicateRowsPerDay: number;
}

export interface FakePeopleState {
  readonly tenants: readonly FakePeopleTenant[];
  readonly settings: PeopleSettings;
  readonly people: FakePerson[];
  readonly links: FakeLink[];
  readonly licenses: FakeLicense[];
  readonly certifications: FakeCertification[];
  readonly skills: PersonSkillResponse[];
  readonly schedules: FakeSchedule[];
  readonly availabilities: FakeAvailability[];
  readonly absences: FakeAbsence[];
  readonly onboarding: OnboardingCaseResponse[];
  readonly offboarding: OffboardingCaseResponse[];
  readonly exceptions: FakeException[];
  readonly matrixVersions: FakeMatrixVersion[];
  readonly approvals: FakeApproval[];
  readonly imports: FakeImportBatch[];
  readonly base: string;
  counter: number;
}

// ---- Identificadores e datas ----

/** Id hexadecimal determinístico por prefixo (campos UUID dos formulários validam o formato). */
export function peopleId(kind: 'person' | 'link' | 'item', n: number): string {
  const prefix = kind === 'person' ? '00000b' : kind === 'link' ? '00000c' : '00000d';
  return `01926f3e-0000-7000-8000-${prefix}${n.toString(16).padStart(6, '0')}`;
}

export function fakePeopleId(
  state: Pick<FakePeopleState, 'counter'> & { counter: number },
): string {
  state.counter += 1;
  return `01926f3e-0000-7000-8000-00000f${state.counter.toString(16).padStart(6, '0')}`;
}

export function etagOf(version: number): string {
  return `W/"${version.toString(16).padStart(16, '0')}"`;
}

export function todayIso(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export { addDays };

export function shiftHours(base: string, hours: number): string {
  return new Date(new Date(base).getTime() + hours * 3_600_000).toISOString();
}

/** Diferença em dias inteiros entre duas datas civis (`to - from`). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

// ---- Identificadores fiscais (módulo 11) ----

function cpfDigit(digits: readonly number[], factor: number): number {
  const sum = digits.reduce((acc, digit, index) => acc + digit * (factor - index), 0);
  const rest = (sum * 10) % 11;
  return rest === 10 ? 0 : rest;
}

export function isValidCpf(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) {
    return false;
  }
  const n = Array.from(digits, Number);
  const first = cpfDigit(n.slice(0, 9), 10);
  const second = cpfDigit([...n.slice(0, 9), first], 11);
  return first === n[9] && second === n[10];
}

/** CPF sintético válido (11 dígitos) a partir de uma semente; nunca um CPF real. */
export function makeCpf(seed: number): string {
  const base = String(100_000_000 + ((seed * 7_919_013) % 899_999_999)).slice(0, 9);
  const digits = Array.from(base, Number);
  const first = cpfDigit(digits, 10);
  const second = cpfDigit([...digits, first], 11);
  return `${base}${String(first)}${String(second)}`;
}

export function formatCpf(digits: string): string {
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function cnpjDigit(digits: readonly number[]): number {
  const weights =
    digits.length === 12
      ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
      : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const sum = digits.reduce((acc, digit, index) => acc + digit * (weights[index] ?? 0), 0);
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
}

export function isValidCnpj(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) {
    return false;
  }
  const n = Array.from(digits, Number);
  const first = cnpjDigit(n.slice(0, 12));
  const second = cnpjDigit([...n.slice(0, 12), first]);
  return first === n[12] && second === n[13];
}

/** CNPJ sintético válido (14 dígitos) a partir de uma semente. */
export function makeCnpj(seed: number): string {
  const base = `${String(10_000_000 + seed * 1237).padStart(8, '0')}0001`;
  const digits = Array.from(base, Number);
  const first = cnpjDigit(digits);
  const second = cnpjDigit([...digits, first]);
  return `${base}${String(first)}${String(second)}`;
}

/** Máscara do identificador fiscal: `***.***.***-NN` só para `BR`; outro país mostra os 4 últimos caracteres. */
export function maskFederalCode(country: string, code: string): string {
  if (country === 'BR') {
    return `***.***.***-${code.slice(-2)}`;
  }
  return `${'*'.repeat(Math.max(code.length - 4, 0))}${code.slice(-4)}`;
}

export function formatFederalCode(country: string, code: string): string {
  return country === 'BR' ? formatCpf(code) : code;
}

// ---- Catálogos (Anexo E.3 — espelham os catálogos do backend) ----

const GCP = 'GCP_ICH_E6';
const ONBOARDING = 'ACADEMIA_ONBOARDING';

function fn(
  code: string,
  name: string,
  cls: string,
  licenses: string[],
  certifications: string[],
): FunctionResponse {
  return {
    code,
    name,
    class: cls,
    status: 'Active',
    requiredLicenseCouncils: licenses,
    requiredCertificationTypes: certifications,
    allowedDelegationTasks: [],
  };
}

export const FUNCTIONS: readonly FunctionResponse[] = [
  fn('BIOMEDICAL', 'Biomédico', 'Clinical', ['CRBM'], [GCP]),
  fn('DATA_ENTRY', 'Entrada de dados', 'Operational', [], [GCP]),
  fn('EVALUATING_PHYSICIAN', 'Médico avaliador', 'Clinical', ['CRM'], [GCP]),
  fn('HOLDING_STAFF', 'Equipe da holding', 'Management', [], [ONBOARDING]),
  fn('INTERN', 'Estagiário', 'Support', [], [ONBOARDING]),
  fn('LAB_TECHNICIAN', 'Técnico de laboratório', 'Clinical', [], [GCP]),
  fn(
    'LEAD_STUDY_COORDINATOR',
    'Coordenador líder de estudos',
    'Operational',
    [],
    [GCP, ONBOARDING],
  ),
  fn('NURSE', 'Enfermeiro', 'Clinical', ['COREN'], [GCP]),
  fn('NURSING_TECHNICIAN', 'Técnico de enfermagem', 'Clinical', ['COREN'], [GCP]),
  fn('OPERATIONS_LEAD', 'Líder de operações', 'Management', [], [ONBOARDING]),
  fn('PHARMACIST_BACKUP', 'Farmacêutico substituto', 'Clinical', ['CRF'], [GCP]),
  fn('PHARMACIST_RT', 'Farmacêutico responsável técnico', 'Clinical', ['CRF'], [GCP]),
  fn('PRINCIPAL_INVESTIGATOR', 'Investigador principal', 'Clinical', ['CRM'], [GCP]),
  fn('QUALITY_ANALYST', 'Analista da qualidade', 'Quality', [], [GCP, ONBOARDING]),
  fn('RECEPTION_SCHEDULER', 'Recepção e agendamento', 'Support', [], [ONBOARDING]),
  fn('REGULATORY_SPECIALIST', 'Especialista regulatório', 'Operational', [], [GCP]),
  fn('STARTUP_SPECIALIST', 'Especialista de start-up', 'Operational', [], [GCP, ONBOARDING]),
  fn('STUDY_COORDINATOR', 'Coordenador de estudos', 'Operational', [], [GCP, ONBOARDING]),
  fn('SUB_INVESTIGATOR', 'Subinvestigador', 'Clinical', ['CRM'], [GCP]),
  fn(
    'TENANT_ADMIN_BOOTSTRAP',
    'Administrador do tenant (bootstrap)',
    'Management',
    [],
    [ONBOARDING],
  ),
];

export const FUNCTION_CODES: ReadonlySet<string> = new Set(FUNCTIONS.map((item) => item.code));

export const LINK_TYPE_LIST: readonly FakeLinkType[] = [
  'Socio',
  'CLT',
  'PJ',
  'CooperadoCPESQ',
  'Estagiario',
];

/** Tipos de vínculo sem jornada (regra 9 da Parte 0.3). */
export const FLEXIBLE_TYPES: ReadonlySet<string> = new Set(['PJ', 'CooperadoCPESQ']);

export const CERTIFICATION_TYPES: readonly CertificationTypeResponse[] = [
  certType(GCP, 'Boas Práticas Clínicas (ICH-E6)', 'Network', 36, 'ICH'),
  certType(ONBOARDING, 'Onboarding Academia GIYUS', 'Network', 24, null),
  certType('LGPD_RESEARCH', 'LGPD aplicada à pesquisa clínica', 'Network', 24, null),
  certType('IATA_SAMPLES', 'Transporte de amostras biológicas (IATA)', 'Network', 24, 'IATA'),
  certType('HELSINKI_ETHICS', 'Declaração de Helsinque — ética em pesquisa', 'Network', 36, null),
  certType('SOP_NETWORK_CORE', 'SOPs centrais da rede', 'Network', 12, null),
  certType('PROTOCOL_TRAINING', 'Treinamento de protocolo', 'Study', null, null),
  certType('BLS_CPR', 'Suporte básico de vida (RCP)', 'Network', 24, null),
];

function certType(
  code: string,
  name: string,
  scope: string,
  months: number | null,
  issuer: string | null,
): CertificationTypeResponse {
  return {
    code,
    name,
    scope,
    defaultValidityMonths: months,
    issuer,
    requiresEvidence: true,
    requiresVerification: true,
  };
}

export const BOARDS: readonly BoardResponse[] = [
  { code: 'CRM', name: 'Conselho Regional de Medicina', country: 'BR' },
  { code: 'COREN', name: 'Conselho Regional de Enfermagem', country: 'BR' },
  { code: 'CRF', name: 'Conselho Regional de Farmácia', country: 'BR' },
  { code: 'CRBM', name: 'Conselho Regional de Biomedicina', country: 'BR' },
  { code: 'CRP', name: 'Conselho Regional de Psicologia', country: 'BR' },
  {
    code: 'CREFITO',
    name: 'Conselho Regional de Fisioterapia e Terapia Ocupacional',
    country: 'BR',
  },
  { code: 'CRN', name: 'Conselho Regional de Nutrição', country: 'BR' },
  { code: 'CRO_DENT', name: 'Conselho Regional de Odontologia', country: 'BR' },
  { code: 'Other', name: 'Outro', country: 'BR' },
];

export const SKILLS: readonly SkillCatalogResponse[] = [
  {
    code: 'ECG_12_LEAD',
    name: 'Eletrocardiograma de 12 derivações',
    category: 'Procedimentos clínicos',
  },
  { code: 'SPIROMETRY', name: 'Espirometria', category: 'Procedimentos clínicos' },
  { code: 'PHLEBOTOMY_ADULT', name: 'Coleta venosa (adulto)', category: 'Coleta de amostras' },
  {
    code: 'PHLEBOTOMY_PEDIATRIC',
    name: 'Coleta venosa (pediátrica)',
    category: 'Coleta de amostras',
  },
  { code: 'INFUSION_ADMIN', name: 'Administração de infusão', category: 'Procedimentos clínicos' },
  {
    code: 'SAMPLE_CENTRIFUGATION',
    name: 'Centrifugação de amostras',
    category: 'Coleta de amostras',
  },
  {
    code: 'COLD_CHAIN_HANDLING',
    name: 'Manuseio de cadeia fria',
    category: 'Logística de amostras',
  },
  {
    code: 'INJECTION_SC_IM',
    name: 'Injeção subcutânea/intramuscular',
    category: 'Procedimentos clínicos',
  },
  {
    code: 'VITAL_SIGNS_MONITORING',
    name: 'Monitorização de sinais vitais',
    category: 'Procedimentos clínicos',
  },
  { code: 'ECHO_ASSIST', name: 'Auxílio em ecocardiograma', category: 'Procedimentos clínicos' },
  { code: 'INTERPRETER_EN', name: 'Interpretação em inglês', category: 'Suporte' },
];

type IndicationRow = Readonly<Record<FakeLinkType, FakeIndication>>;

function row(
  socio: FakeIndication,
  clt: FakeIndication,
  pj: FakeIndication,
  cooperado: FakeIndication,
  estagiario: FakeIndication = 'NotAllowed',
): IndicationRow {
  return { Socio: socio, CLT: clt, PJ: pj, CooperadoCPESQ: cooperado, Estagiario: estagiario };
}

const PRINCIPAL = row('Indicated', 'NotAllowed', 'Indicated', 'Indicated');
const INVESTIGATORS = row('CaseByCase', 'CaseByCase', 'Indicated', 'Indicated');
const COORDINATORS = row('CaseByCase', 'CaseByCase', 'CaseByCase', 'Indicated');
const PHARMACISTS = row('CaseByCase', 'CaseByCase', 'CaseByCase', 'CaseByCase');
const CLINICAL_STAFF = row('CaseByCase', 'CaseByCase', 'CaseByCase', 'Indicated');
const CENTRAL = row('Indicated', 'Indicated', 'CaseByCase', 'NotAllowed');
const INTERN = row('NotAllowed', 'NotAllowed', 'NotAllowed', 'NotAllowed', 'Indicated');

const MATRIX_ROWS: Readonly<Record<string, IndicationRow>> = {
  PRINCIPAL_INVESTIGATOR: PRINCIPAL,
  SUB_INVESTIGATOR: INVESTIGATORS,
  EVALUATING_PHYSICIAN: INVESTIGATORS,
  LEAD_STUDY_COORDINATOR: COORDINATORS,
  STUDY_COORDINATOR: COORDINATORS,
  PHARMACIST_RT: PHARMACISTS,
  PHARMACIST_BACKUP: PHARMACISTS,
  NURSE: CLINICAL_STAFF,
  NURSING_TECHNICIAN: CLINICAL_STAFF,
  BIOMEDICAL: CLINICAL_STAFF,
  LAB_TECHNICIAN: CLINICAL_STAFF,
  DATA_ENTRY: CENTRAL,
  RECEPTION_SCHEDULER: CENTRAL,
  QUALITY_ANALYST: CENTRAL,
  REGULATORY_SPECIALIST: CENTRAL,
  STARTUP_SPECIALIST: CENTRAL,
  OPERATIONS_LEAD: CENTRAL,
  HOLDING_STAFF: CENTRAL,
  INTERN,
  TENANT_ADMIN_BOOTSTRAP: CENTRAL,
};

/** Matriz v1 completa: toda função × os cinco tipos de vínculo (Anexo E.3). */
export function seedMatrixEntries(): MatrixEntryResponse[] {
  return FUNCTIONS.flatMap((item) =>
    LINK_TYPE_LIST.map((linkType): MatrixEntryResponse => ({
      functionCode: item.code,
      linkType,
      indication: (MATRIX_ROWS[item.code] ?? CENTRAL)[linkType],
    })),
  );
}

/**
 * Matriz em vigor hoje (OPEN-097): a aprovada (`Published` ou `Superseded`) de maior vigência não posterior a hoje — empate, a de maior número. Uma versão
 * agendada (vigência futura) ainda não conta.
 */
export function publishedMatrix(state: FakePeopleState): FakeMatrixVersion | undefined {
  const today = todayIso();
  return state.matrixVersions
    .filter(
      (version) =>
        (version.status === 'Published' || version.status === 'Superseded') &&
        version.effectiveFrom !== null &&
        version.effectiveFrom <= today,
    )
    .sort(
      (a, b) =>
        (b.effectiveFrom ?? '').localeCompare(a.effectiveFrom ?? '') || b.version - a.version,
    )[0];
}

export function indicationOf(
  state: FakePeopleState,
  functionCode: string,
  linkType: string,
): FakeIndication {
  const entry = publishedMatrix(state)?.entries.find(
    (item) => item.functionCode === functionCode && item.linkType === linkType,
  );
  const value = entry?.indication;
  return value === 'Indicated' || value === 'NotAllowed' ? value : 'CaseByCase';
}

// ---- Cálculos de situação ----

/** `Scheduled | Active | Expired | Ended` na data (como `PersonLink.StatusOn`). */
export function linkStatusOn(
  link: Pick<PersonLinkResponse, 'effectiveFrom' | 'effectiveTo' | 'endedAt'>,
  date: string,
): string {
  if (link.endedAt) {
    return 'Ended';
  }
  if (link.effectiveFrom > date) {
    return 'Scheduled';
  }
  return link.effectiveTo !== null && link.effectiveTo < date ? 'Expired' : 'Active';
}

/** `Valid | Expiring | Expired` pela validade e pelo maior limiar de alerta (T-60). */
export function credentialStatus(
  validUntil: string,
  today: string,
  alertDays: readonly number[],
): 'Valid' | 'Expiring' | 'Expired' {
  if (validUntil < today) {
    return 'Expired';
  }
  return daysBetween(today, validUntil) <= Math.max(...alertDays) ? 'Expiring' : 'Valid';
}

export function displayNameOf(
  person: Pick<FakePerson, 'givenName' | 'familyName' | 'socialName'>,
): string {
  return person.socialName ?? `${person.givenName} ${person.familyName}`;
}

// ---- Conversões para o contrato ----

export function toPersonSummary(person: FakePerson): PersonSummaryResponse {
  return {
    id: person.id,
    displayName: displayNameOf(person),
    givenName: person.givenName,
    familyName: person.familyName,
    socialName: person.socialName,
    preferredLocale: person.preferredLocale,
    status: person.status,
    createdAt: person.createdAt,
  };
}

export function toPersonDetail(
  person: FakePerson,
  visibility: readonly string[],
): PersonDetailResponse {
  return {
    id: person.id,
    displayName: displayNameOf(person),
    givenName: person.givenName,
    familyName: person.familyName,
    socialName: person.socialName,
    country: person.country,
    federalCodeMasked:
      person.federalCode === null ? null : maskFederalCode(person.country, person.federalCode),
    isPending: person.federalCode === null,
    preferredLocale: person.preferredLocale,
    photoDocumentId: person.photoDocumentId,
    status: person.status,
    contacts: person.contacts.map((contact) => ({ ...contact })),
    visibility: [...visibility],
    createdAt: person.createdAt,
    modifiedAt: person.modifiedAt,
  };
}

export function toLinkResponse(link: FakeLink, today: string): PersonLinkResponse {
  const { version: _version, ...rest } = link;
  return {
    ...rest,
    additionalSiteIds: [...link.additionalSiteIds],
    status: linkStatusOn(link, today),
  };
}

export function toMatrixSummary(version: FakeMatrixVersion): MatrixVersionSummaryResponse {
  const { entries: _entries, rowVersion: _rowVersion, ...rest } = version;
  return rest;
}

export function toMatrixResponse(version: FakeMatrixVersion): MatrixVersionResponse {
  const { rowVersion: _rowVersion, ...rest } = version;
  return { ...rest, entries: version.entries.map((entry) => ({ ...entry })) };
}

export function toApprovalResponse(approval: FakeApproval): TwoKeyApprovalResponse {
  const { firstActorRoles: _roles, ...rest } = approval;
  return rest;
}

// ---- Checklists (OnboardingItemCatalog / OffboardingItemCatalog) ----

function pending(code: string, description: string): ChecklistItemResponse {
  return {
    code,
    description,
    status: 'Pending',
    completedAt: null,
    completedBy: null,
    evidenceDocumentId: null,
  };
}

/** Quatro itens fixos + um por conselho e por tipo de certificação que a função exige. */
export function buildOnboardingItems(functionCode: string): ChecklistItemResponse[] {
  const definition = FUNCTIONS.find((item) => item.code === functionCode);
  return [
    pending('PersonalDocuments', 'Documentos pessoais'),
    pending('Contract', 'Contrato/ato/termo de vínculo'),
    pending('UserInvitation', 'Convite de usuário enviado'),
    pending('InitialTraining', 'Treinamento inicial'),
    ...(definition?.requiredLicenseCouncils ?? []).map((council) =>
      pending(`License:${council}`, `Licença profissional (${council})`),
    ),
    ...(definition?.requiredCertificationTypes ?? []).map((type) =>
      pending(`Certification:${type}`, `Certificação (${type})`),
    ),
  ];
}

export function buildOffboardingItems(): ChecklistItemResponse[] {
  return [
    pending('AccessRevocationConfirmed', 'Revogação de acesso confirmada'),
    pending('EquipmentReturned', 'Equipamentos devolvidos'),
    pending('BadgeReturned', 'Crachá/credencial devolvido'),
    pending('KnowledgeTransferCompleted', 'Transferência de conhecimento concluída'),
    pending('ExitInterviewCompleted', 'Entrevista de desligamento realizada'),
  ];
}

// ---- Auditoria ----

/** Usuário "do sistema" que registrou os dados semeados (nunca coincide com um principal dos cenários). */
export const SEED_ACTOR = '01926f3e-0000-7000-8000-00000000a0a0';

// ---- Semente ----

export interface Seeder {
  readonly state: FakePeopleState;
  readonly today: string;
  readonly createdAt: string;
}

function weeklyOf(
  days: Partial<Record<string, [string, string][]>>,
): Record<string, { start: string; end: string }[]> {
  const result: Record<string, { start: string; end: string }[]> = {};
  for (const day of [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ]) {
    result[day] = (days[day] ?? []).map(([start, end]) => ({ start, end }));
  }
  return result;
}

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;

function everyWeekday(
  windows: [string, string][],
): Record<string, { start: string; end: string }[]> {
  return weeklyOf(Object.fromEntries(WEEKDAYS.map((day) => [day, windows])));
}

export interface SeedPersonInput {
  readonly id: string;
  readonly given: string;
  readonly family: string;
  readonly social?: string | null;
  readonly cpfSeed: number | null;
  readonly birthYear: number;
  readonly email: string;
  readonly phone?: string | null;
  readonly locale?: string;
  readonly visibility?: Record<string, VisibilityReason[]>;
  readonly status?: 'Active' | 'Inactive';
}

export function seedPerson(seeder: Seeder, input: SeedPersonInput): FakePerson {
  const contacts: PersonContactResponse[] = [
    {
      id: fakePeopleId(seeder.state),
      kind: 'Email',
      value: input.email,
      isPrimary: true,
      verifiedAt: seeder.createdAt,
    },
  ];
  if (input.phone) {
    contacts.push({
      id: fakePeopleId(seeder.state),
      kind: 'Phone',
      value: input.phone,
      isPrimary: true,
      verifiedAt: null,
    });
  }
  const person: FakePerson = {
    id: input.id,
    givenName: input.given,
    familyName: input.family,
    socialName: input.social ?? null,
    country: 'BR',
    federalCode: input.cpfSeed === null ? null : makeCpf(input.cpfSeed),
    birthDate:
      input.cpfSeed === null
        ? null
        : `${String(input.birthYear)}-0${String((input.cpfSeed % 9) + 1)}-1${String(input.cpfSeed % 9)}`,
    preferredLocale: input.locale ?? 'pt-BR',
    photoDocumentId: null,
    status: input.status ?? 'Active',
    contacts,
    createdAt: seeder.createdAt,
    modifiedAt: null,
    version: 1,
    visibility: { ...(input.visibility ?? {}) },
  };
  seeder.state.people.push(person);
  return person;
}

export interface SeedLinkInput {
  readonly id: string;
  readonly person: FakePerson;
  readonly legalEntityId: string;
  readonly tenantId: string | null;
  readonly linkType: FakeLinkType;
  readonly functionCode: string;
  readonly fromDaysAgo: number;
  readonly endedDaysAgo?: number;
  readonly classification?: 'Valid' | 'PendingOpinion';
  readonly justification?: string;
  readonly siteId?: string | null;
  readonly cnpjSeed?: number;
  readonly memberNumber?: string;
}

export function seedLink(seeder: Seeder, input: SeedLinkInput): FakeLink {
  const { today } = seeder;
  const ended = input.endedDaysAgo !== undefined;
  const link: FakeLink = {
    id: input.id,
    personId: input.person.id,
    legalEntityId: input.legalEntityId,
    tenantId: input.tenantId,
    linkType: input.linkType,
    functionCode: input.functionCode,
    effectiveFrom: addDays(today, -input.fromDaysAgo),
    effectiveTo: ended ? addDays(today, -(input.endedDaysAgo ?? 0)) : null,
    status: ended ? 'Ended' : 'Active',
    classificationStatus: input.classification ?? 'Valid',
    classificationJustification: input.justification ?? null,
    legalOpinionDocumentId: null,
    contractDocumentId: null,
    pjCnpj: input.linkType === 'PJ' ? formatCnpj(makeCnpj(input.cnpjSeed ?? 1)) : null,
    cooperativeMemberNumber:
      input.linkType === 'CooperadoCPESQ' ? (input.memberNumber ?? 'COOP-0001') : null,
    primarySiteId: input.siteId ?? null,
    additionalSiteIds: [],
    endReason: ended ? 'Encerramento de contrato' : null,
    endedAt: ended
      ? new Date(`${addDays(today, -(input.endedDaysAgo ?? 0))}T12:00:00Z`).toISOString()
      : null,
    endedBy: ended ? SEED_ACTOR : null,
    createdAt: seeder.createdAt,
    modifiedAt: null,
    version: 1,
  };
  seeder.state.links.push(link);
  if (input.tenantId !== null) {
    const reasons = input.person.visibility[input.tenantId] ?? [];
    if (!reasons.includes('Link')) {
      input.person.visibility[input.tenantId] = [...reasons, 'Link'];
    }
  }
  return link;
}

export function seedLicense(
  seeder: Seeder,
  person: FakePerson,
  council: string,
  state: string,
  number: string,
  validInDays: number,
): void {
  seeder.state.licenses.push({
    id: fakePeopleId(seeder.state),
    personId: person.id,
    council,
    state,
    number,
    validUntil: addDays(seeder.today, validInDays),
    status: credentialStatus(
      addDays(seeder.today, validInDays),
      seeder.today,
      seeder.state.settings.alertDays,
    ),
    documentId: null,
    createdAt: seeder.createdAt,
    modifiedAt: null,
    version: 1,
  });
}

export function seedCertification(
  seeder: Seeder,
  person: FakePerson,
  typeCode: string,
  issuedDaysAgo: number,
  validInDays: number,
  verified: boolean,
): void {
  const validUntil = addDays(seeder.today, validInDays);
  seeder.state.certifications.push({
    id: fakePeopleId(seeder.state),
    personId: person.id,
    typeCode,
    studyId: null,
    issuedAt: addDays(seeder.today, -issuedDaysAgo),
    validUntil,
    status: credentialStatus(validUntil, seeder.today, seeder.state.settings.alertDays),
    evidenceDocumentId: fakePeopleId(seeder.state),
    origin: 'Manual',
    verifiedBy: verified ? SEED_ACTOR : null,
    verifiedAt: verified ? seeder.createdAt : null,
    revokedAt: null,
    createdBy: SEED_ACTOR,
    createdAt: seeder.createdAt,
    modifiedAt: null,
    version: 1,
  });
}

function seedSchedule(
  seeder: Seeder,
  link: FakeLink,
  weekly: Record<string, { start: string; end: string }[]>,
): void {
  seeder.state.schedules.push({
    id: fakePeopleId(seeder.state),
    personId: link.personId,
    personLinkId: link.id,
    siteId: link.primarySiteId ?? FIXTURE_IDS.sitePilotSede,
    effectiveFrom: link.effectiveFrom,
    effectiveTo: null,
    weekly,
    createdAt: seeder.createdAt,
    modifiedAt: null,
    version: 1,
  });
}

function seedAvailability(
  seeder: Seeder,
  link: FakeLink,
  input: {
    kind: 'Weekly' | 'Dated';
    weekly?: Record<string, { start: string; end: string }[]>;
    date?: string;
    start?: string;
    end?: string;
  },
): void {
  seeder.state.availabilities.push({
    id: fakePeopleId(seeder.state),
    personId: link.personId,
    personLinkId: link.id,
    kind: input.kind,
    weekly: input.kind === 'Weekly' ? (input.weekly ?? weeklyOf({})) : null,
    date: input.kind === 'Dated' ? (input.date ?? seeder.today) : null,
    startTime: input.kind === 'Dated' ? (input.start ?? '08:00') : null,
    endTime: input.kind === 'Dated' ? (input.end ?? '12:00') : null,
    siteId: link.primarySiteId,
    declaredBy: SEED_ACTOR,
    declaredAt: seeder.createdAt,
    createdAt: seeder.createdAt,
    modifiedAt: null,
    version: 1,
  });
}

function seedAbsence(
  seeder: Seeder,
  person: FakePerson,
  kind: string,
  fromInDays: number,
  toInDays: number,
  note: string | null,
): void {
  seeder.state.absences.push({
    id: fakePeopleId(seeder.state),
    personId: person.id,
    tenantId: null, // origem: a rede (cada tenant só vê as ausências que registrou — OPEN-098)
    kind,
    startDate: addDays(seeder.today, fromInDays),
    endDate: addDays(seeder.today, toInDays),
    startTime: null,
    endTime: null,
    blocksScheduling: true,
    note,
    createdAt: seeder.createdAt,
    createdBy: SEED_ACTOR,
    removed: false,
  });
}

function seedOnboarding(
  seeder: Seeder,
  link: FakeLink,
  doneCodes: readonly string[] | 'all',
): void {
  const items = buildOnboardingItems(link.functionCode).map((item): ChecklistItemResponse =>
    doneCodes === 'all' || doneCodes.includes(item.code)
      ? { ...item, status: 'Done', completedAt: seeder.createdAt, completedBy: SEED_ACTOR }
      : item,
  );
  const ready = items.every((item) => item.status !== 'Pending');
  seeder.state.onboarding.push({
    id: fakePeopleId(seeder.state),
    personId: link.personId,
    personLinkId: link.id,
    items,
    status: ready ? 'ReadyToOperate' : 'InProgress',
    startedAt: seeder.createdAt,
    readyToOperateAt: ready ? seeder.createdAt : null,
  });
}

function seedOffboarding(seeder: Seeder, link: FakeLink, doneCodes: readonly string[]): void {
  const items = buildOffboardingItems().map((item): ChecklistItemResponse =>
    doneCodes.includes(item.code)
      ? { ...item, status: 'Done', completedAt: seeder.createdAt, completedBy: SEED_ACTOR }
      : item,
  );
  const accessRevoked = doneCodes.includes('AccessRevocationConfirmed');
  seeder.state.offboarding.push({
    id: fakePeopleId(seeder.state),
    personId: link.personId,
    personLinkId: link.id,
    items,
    status: items.every((item) => item.status === 'Done') ? 'Completed' : 'InProgress',
    startedAt: link.endedAt ?? seeder.createdAt,
    accessRevokedAt: accessRevoked ? seeder.createdAt : null,
  });
}

function splitName(displayName: string): { given: string; family: string } {
  const [given = displayName, ...rest] = displayName.trim().split(/\s+/);
  return { given, family: rest.join(' ') || given };
}

/** Nome da pessoa jurídica da holding no fake (seletor de pessoa jurídica; OPEN-095). */
export const HOLDING_LEGAL_NAME = 'GIYUS Holding Participações Ltda.';

const TENANTS: readonly FakePeopleTenant[] = [
  {
    id: FIXTURE_IDS.tenantPilot,
    code: 'PILOT',
    legalName: 'Centro Piloto Pesquisa Clínica Ltda.',
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    siteIds: [FIXTURE_IDS.sitePilotSede, FIXTURE_IDS.sitePilotAnexo],
    siteCodes: { SEDE: FIXTURE_IDS.sitePilotSede, ANEXO: FIXTURE_IDS.sitePilotAnexo },
  },
  {
    id: FIXTURE_IDS.tenantSecond,
    code: 'SECOND',
    legalName: 'Segundo Centro Clínico Ltda.',
    legalEntityId: FIXTURE_IDS.legalEntitySecond,
    siteIds: [FIXTURE_IDS.siteSecondUnidade],
    siteCodes: { 'UNIDADE-1': FIXTURE_IDS.siteSecondUnidade },
  },
];

export const PEOPLE_IDS = {
  helena: peopleId('person', 1),
  marcos: peopleId('person', 2),
  beatriz: peopleId('person', 3),
  rafael: peopleId('person', 4),
  camila: peopleId('person', 5),
  tania: peopleId('person', 6),
  diego: peopleId('person', 7),
  paulo: peopleId('person', 8),
  elisa: peopleId('person', 9),
  fernanda: peopleId('person', 10),
  alberto: peopleId('person', 11),
  /** Vínculos que os testes usam diretamente. */
  linkHelena: peopleId('link', 1),
  linkMarcos: peopleId('link', 2),
  linkBeatriz: peopleId('link', 3),
  linkRafael: peopleId('link', 4),
  linkCamila: peopleId('link', 5),
  linkTaniaClt: peopleId('link', 6),
  linkDiegoClt: peopleId('link', 7),
  linkPaulo: peopleId('link', 8),
  linkElisa: peopleId('link', 9),
  linkFernanda: peopleId('link', 10),
  linkAlberto: peopleId('link', 11),
  matrixV1: peopleId('item', 1),
  /** Proposta do RH aguardando a segunda chave, semeada para quem pode dá-la (Jurídico); a aprovação é `approvalMatrixV2`. */
  matrixV2: peopleId('item', 2),
  approvalMatrixV2: peopleId('item', 3),
} as const;

export function seedPeopleState(me: MeResponse): FakePeopleState {
  const base = new Date().toISOString();
  const state: FakePeopleState = {
    tenants: TENANTS,
    settings: {
      minIntervalDays: 180,
      internMaxDailyHours: 6,
      alertDays: [60, 30, 7],
      importMaxRows: 500,
      importMaxDuplicateRows: 10,
      importMaxDuplicateRowsPerDay: 50,
    },
    people: [],
    links: [],
    licenses: [],
    certifications: [],
    skills: [],
    schedules: [],
    availabilities: [],
    absences: [],
    onboarding: [],
    offboarding: [],
    exceptions: [],
    matrixVersions: [],
    approvals: [],
    imports: [],
    base,
    counter: 0,
  };
  const seeder: Seeder = {
    state,
    today: todayIso(),
    createdAt: new Date(Date.parse(base) - 86_400_000 * 90).toISOString(),
  };
  const pilot = FIXTURE_IDS.tenantPilot;
  const second = FIXTURE_IDS.tenantSecond;
  const sede = FIXTURE_IDS.sitePilotSede;

  state.matrixVersions.push({
    id: PEOPLE_IDS.matrixV1,
    version: 1,
    rowVersion: 1,
    status: 'Published',
    changeSummary:
      'Matriz de Enquadramento v1 (Anexo E.3, BP 8.3); parecer A13 pendente (OPEN-041).',
    effectiveFrom: addDays(seeder.today, -90),
    opinionDocumentId: null,
    requestedBy: null,
    secondKeyBy: null,
    twoKeyApprovalId: null,
    publishedAt: seeder.createdAt,
    supersededAt: null,
    createdAt: seeder.createdAt,
    entries: seedMatrixEntries(),
  });

  // Modo Greenfield de Produção (syllas@giyus.com.br): mantém exclusivamente o cadastro da pessoa do administrador logado
  if (
    me.user.id === '01926f3e-0000-7000-8000-000000000099' ||
    me.user.email.toLowerCase() === 'syllas@giyus.com.br'
  ) {
    (state as { tenants: readonly FakePeopleTenant[] }).tenants = [];
    seedOwnPerson(seeder, me, false);
    return state;
  }

  // 1. Investigadora principal, sócia do centro: escala fixa, licença e certificações vigentes.
  const helena = seedPerson(seeder, {
    id: PEOPLE_IDS.helena,
    given: 'Helena',
    family: 'Prado',
    cpfSeed: 1,
    birthYear: 1975,
    email: 'helena.prado@centro.example',
    phone: '+55 41 99999-0001',
  });
  const helenaLink = seedLink(seeder, {
    id: PEOPLE_IDS.linkHelena,
    person: helena,
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    tenantId: pilot,
    linkType: 'Socio',
    functionCode: 'PRINCIPAL_INVESTIGATOR',
    fromDaysAgo: 700,
    siteId: sede,
  });
  seedLicense(seeder, helena, 'CRM', 'PR', '12345', 400);
  seedCertification(seeder, helena, GCP, 240, 850, true);
  seedCertification(seeder, helena, 'HELSINKI_ETHICS', 200, 900, true);
  seedSchedule(
    seeder,
    helenaLink,
    weeklyOf({
      monday: [['08:00', '12:00']],
      wednesday: [['08:00', '12:00']],
      friday: [['08:00', '12:00']],
    }),
  );
  seedOnboarding(seeder, helenaLink, 'all');

  // 2. Enfermeiro CLT: certificação GCP a vencer (Expiring), onboarding em andamento, escala fixa.
  const marcos = seedPerson(seeder, {
    id: PEOPLE_IDS.marcos,
    given: 'Marcos',
    family: 'Teixeira',
    cpfSeed: 2,
    birthYear: 1988,
    email: 'marcos.teixeira@centro.example',
    phone: '+55 41 99999-0002',
  });
  const marcosLink = seedLink(seeder, {
    id: PEOPLE_IDS.linkMarcos,
    person: marcos,
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    tenantId: pilot,
    linkType: 'CLT',
    functionCode: 'NURSE',
    fromDaysAgo: 420,
    siteId: sede,
    justification: 'Enfermagem de rotina do centro.',
    classification: 'Valid',
  });
  seedLicense(seeder, marcos, 'COREN', 'PR', '987654', 200);
  seedCertification(seeder, marcos, GCP, 1060, 25, true);
  seedCertification(seeder, marcos, 'BLS_CPR', 300, 430, true);
  state.skills.push({
    id: fakePeopleId(state),
    personId: marcos.id,
    skillCode: 'VITAL_SIGNS_MONITORING',
    level: 'Avançado',
    evidenceDocumentId: null,
    validUntil: null,
    createdAt: seeder.createdAt,
    modifiedAt: null,
  });
  seedSchedule(
    seeder,
    marcosLink,
    everyWeekday([
      ['08:00', '12:00'],
      ['13:00', '17:00'],
    ]),
  );
  seedOnboarding(seeder, marcosLink, [
    'PersonalDocuments',
    'Contract',
    'UserInvitation',
    'InitialTraining',
    'License:COREN',
  ]);

  // 3. Coordenadora cooperada: GCP vencida (Expired), disponibilidade semanal e férias futuras.
  const beatriz = seedPerson(seeder, {
    id: PEOPLE_IDS.beatriz,
    given: 'Beatriz',
    family: 'Nogueira',
    cpfSeed: 3,
    birthYear: 1990,
    email: 'beatriz.nogueira@centro.example',
    phone: '+55 41 99999-0003',
    social: 'Bia Nogueira',
  });
  const beatrizLink = seedLink(seeder, {
    id: PEOPLE_IDS.linkBeatriz,
    person: beatriz,
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    tenantId: pilot,
    linkType: 'CooperadoCPESQ',
    functionCode: 'STUDY_COORDINATOR',
    fromDaysAgo: 300,
    siteId: sede,
    memberNumber: 'COOP-0042',
  });
  seedCertification(seeder, beatriz, GCP, 1100, -20, true);
  seedCertification(seeder, beatriz, ONBOARDING, 100, 630, true);
  seedAvailability(seeder, beatrizLink, {
    kind: 'Weekly',
    weekly: weeklyOf({
      monday: [['09:00', '15:00']],
      wednesday: [['09:00', '15:00']],
      friday: [['09:00', '15:00']],
    }),
  });
  seedAbsence(seeder, beatriz, 'Vacation', 30, 44, 'Férias programadas.');
  seedOnboarding(seeder, beatrizLink, 'all');

  // 4. Biomédico PJ: enquadramento "caso a caso" sem parecer (PendingOpinion), CRBM a vencer, certificação sem verificar.
  const rafael = seedPerson(seeder, {
    id: PEOPLE_IDS.rafael,
    given: 'Rafael',
    family: 'Lima',
    cpfSeed: 4,
    birthYear: 1985,
    email: 'rafael.lima@centro.example',
  });
  const rafaelLink = seedLink(seeder, {
    id: PEOPLE_IDS.linkRafael,
    person: rafael,
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    tenantId: pilot,
    linkType: 'PJ',
    functionCode: 'BIOMEDICAL',
    fromDaysAgo: 120,
    siteId: sede,
    cnpjSeed: 4,
    classification: 'PendingOpinion',
    justification: 'Consultoria especializada em coleta, sem exclusividade.',
  });
  seedLicense(seeder, rafael, 'CRBM', 'PR', '55501', 20);
  seedCertification(seeder, rafael, 'IATA_SAMPLES', 60, 670, false);
  seedAvailability(seeder, rafaelLink, {
    kind: 'Dated',
    date: addDays(seeder.today, 7),
    start: '08:00',
    end: '12:00',
  });

  // 5. Estagiária: escala de 4 h por dia, sem onboarding iniciado.
  const camila = seedPerson(seeder, {
    id: PEOPLE_IDS.camila,
    given: 'Camila',
    family: 'Souza',
    cpfSeed: 5,
    birthYear: 2002,
    email: 'camila.souza@centro.example',
  });
  const camilaLink = seedLink(seeder, {
    id: PEOPLE_IDS.linkCamila,
    person: camila,
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    tenantId: pilot,
    linkType: 'Estagiario',
    functionCode: 'INTERN',
    fromDaysAgo: 60,
    siteId: sede,
  });
  seedSchedule(seeder, camilaLink, everyWeekday([['08:00', '12:00']]));

  // 6. Ex-CLT há mais de 180 dias (exceção de transição possível) e 7. ex-CLT há 90 dias (intervalo não decorrido).
  const tania = seedPerson(seeder, {
    id: PEOPLE_IDS.tania,
    given: 'Tânia',
    family: 'Ribeiro',
    cpfSeed: 6,
    birthYear: 1983,
    email: 'tania.ribeiro@centro.example',
  });
  const taniaLink = seedLink(seeder, {
    id: PEOPLE_IDS.linkTaniaClt,
    person: tania,
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    tenantId: pilot,
    linkType: 'CLT',
    functionCode: 'NURSING_TECHNICIAN',
    fromDaysAgo: 900,
    endedDaysAgo: 400,
    siteId: sede,
  });
  seedOffboarding(seeder, taniaLink, [
    'AccessRevocationConfirmed',
    'EquipmentReturned',
    'BadgeReturned',
    'KnowledgeTransferCompleted',
    'ExitInterviewCompleted',
  ]);
  const diego = seedPerson(seeder, {
    id: PEOPLE_IDS.diego,
    given: 'Diego',
    family: 'Alves',
    cpfSeed: 7,
    birthYear: 1992,
    email: 'diego.alves@centro.example',
  });
  const diegoLink = seedLink(seeder, {
    id: PEOPLE_IDS.linkDiegoClt,
    person: diego,
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    tenantId: pilot,
    linkType: 'CLT',
    functionCode: 'LAB_TECHNICIAN',
    fromDaysAgo: 500,
    endedDaysAgo: 90,
    siteId: sede,
  });
  seedOffboarding(seeder, diegoLink, ['AccessRevocationConfirmed']);

  // 8. Funcionário da holding (visível só à rede) e 9. especialista com atribuição no centro (razão `Assignment`).
  const paulo = seedPerson(seeder, {
    id: PEOPLE_IDS.paulo,
    given: 'Paulo',
    family: 'Henrique Costa',
    cpfSeed: 8,
    birthYear: 1979,
    email: 'paulo.costa@giyus.com.br',
  });
  seedLink(seeder, {
    id: PEOPLE_IDS.linkPaulo,
    person: paulo,
    legalEntityId: HOLDING_LEGAL_ENTITY_ID,
    tenantId: null,
    linkType: 'CLT',
    functionCode: 'OPERATIONS_LEAD',
    fromDaysAgo: 800,
  });
  const elisa = seedPerson(seeder, {
    id: PEOPLE_IDS.elisa,
    given: 'Elisa',
    family: 'Startup',
    cpfSeed: 9,
    birthYear: 1987,
    email: 'elisa.startup@giyus.com.br',
    visibility: { [pilot]: ['Assignment'] },
  });
  seedLink(seeder, {
    id: PEOPLE_IDS.linkElisa,
    person: elisa,
    legalEntityId: HOLDING_LEGAL_ENTITY_ID,
    tenantId: null,
    linkType: 'CLT',
    functionCode: 'STARTUP_SPECIALIST',
    fromDaysAgo: 365,
  });

  // 10. Pessoa do outro centro (não visível ao PILOT) e 11. pessoa pendente do bootstrap (sem identificador fiscal).
  const fernanda = seedPerson(seeder, {
    id: PEOPLE_IDS.fernanda,
    given: 'Fernanda',
    family: 'Costa',
    cpfSeed: 10,
    birthYear: 1991,
    email: 'fernanda.costa@parceiro.example',
  });
  seedLink(seeder, {
    id: PEOPLE_IDS.linkFernanda,
    person: fernanda,
    legalEntityId: FIXTURE_IDS.legalEntitySecond,
    tenantId: second,
    linkType: 'PJ',
    functionCode: 'STUDY_COORDINATOR',
    fromDaysAgo: 200,
    siteId: FIXTURE_IDS.siteSecondUnidade,
    cnpjSeed: 10,
    classification: 'PendingOpinion',
    justification: 'Coordenação por projeto.',
  });
  const alberto = seedPerson(seeder, {
    id: PEOPLE_IDS.alberto,
    given: 'Alberto',
    family: 'Admin',
    cpfSeed: null,
    birthYear: 1980,
    email: 'alberto.admin@centro.example',
  });
  seedLink(seeder, {
    id: PEOPLE_IDS.linkAlberto,
    person: alberto,
    legalEntityId: FIXTURE_IDS.legalEntityPilot,
    tenantId: pilot,
    linkType: 'CLT',
    functionCode: 'TENANT_ADMIN_BOOTSTRAP',
    fromDaysAgo: 30,
    siteId: sede,
  });

  seedOwnPerson(seeder, me, true);
  if (me.networkAccess.roles.includes('LEGAL')) {
    seedPendingMatrixProposal(seeder);
  }
  return state;
}

/**
 * Para o Jurídico (segunda chave) já existe uma proposta do RH esperando: o estado do backend falso é descartado a cada
 * carga da página, então a primeira chave de outra sessão não chegaria até ele. Mesma regra do servidor: outra pessoa e outro
 * papel (RH, `PEOPLE_ADMIN`). A proposta torna o investigador principal CLT "caso a caso".
 */
function seedPendingMatrixProposal(seeder: Seeder): void {
  const { state } = seeder;
  const now = new Date().toISOString();
  state.matrixVersions.push({
    id: PEOPLE_IDS.matrixV2,
    version: 2,
    rowVersion: 1,
    status: 'PendingSecondKey',
    changeSummary:
      'Investigador principal em CLT passa a caso a caso, conforme o parecer do Jurídico.',
    effectiveFrom: null,
    opinionDocumentId: '01926f3e-0000-7000-8000-00000000d0c9',
    requestedBy: FIXTURE_PEOPLE.adminUser,
    secondKeyBy: null,
    twoKeyApprovalId: PEOPLE_IDS.approvalMatrixV2,
    publishedAt: null,
    supersededAt: null,
    createdAt: now,
    entries: seedMatrixEntries().map((entry) =>
      entry.functionCode === 'PRINCIPAL_INVESTIGATOR' && entry.linkType === 'CLT'
        ? { ...entry, indication: 'CaseByCase' }
        : entry,
    ),
  });
  state.approvals.push({
    id: PEOPLE_IDS.approvalMatrixV2,
    actionType: 'people.matrix.manage',
    objectType: 'FunctionLinkMatrixVersion',
    objectId: PEOPLE_IDS.matrixV2,
    status: 'PendingSecondKey',
    firstActorUserId: FIXTURE_PEOPLE.adminUser,
    firstAt: now,
    expiresAt: shiftHours(now, 72),
    secondActorUserId: null,
    secondAt: null,
    requiredSecondPermission: 'people.matrix.manage',
    requireDistinctRole: true,
    firstActorRoles: ['PEOPLE_ADMIN'],
  });
}

/** A pessoa da claim `pid` do principal ("Meu cadastro"): seus vínculos de `/me` viram vínculos reais, um cartão por pessoa jurídica. */
function seedOwnPerson(seeder: Seeder, me: MeResponse, includeDemoAbsence = true): void {
  const own = me.person;
  if (!own || seeder.state.people.some((person) => person.id === own.id)) {
    return;
  }
  const isSyllas = me.user.email.toLowerCase() === 'syllas@giyus.com.br';
  const names = isSyllas
    ? { given: 'Syllas', family: 'de Souza' }
    : splitName(me.user.displayName);
  const person = seedPerson(seeder, {
    id: own.id,
    given: names.given,
    family: names.family,
    ...(isSyllas ? { social: 'Syllas de Souza' } : {}),
    cpfSeed: 40 + seeder.state.people.length,
    birthYear: 1986,
    email: me.user.email,
    locale: me.user.preferredLocale,
  });
  own.activeLinks.forEach((item, index) => {
    const type = LINK_TYPE_LIST.find((candidate) => candidate === item.linkType) ?? 'CLT';
    const link = seedLink(seeder, {
      id: item.id,
      person,
      legalEntityId: item.legalEntityId,
      tenantId: item.tenantId,
      linkType: type,
      functionCode: item.functionCode,
      fromDaysAgo: 365 - index * 40,
      siteId: item.primarySiteId,
      cnpjSeed: 60 + index,
      memberNumber: `COOP-${String(100 + index)}`,
    });
    if (FLEXIBLE_TYPES.has(type) && includeDemoAbsence) {
      seedAvailability(seeder, link, {
        kind: 'Weekly',
        weekly: weeklyOf({ tuesday: [['09:00', '13:00']], thursday: [['09:00', '13:00']] }),
      });
    }
  });
  if (includeDemoAbsence) {
    seedCertification(seeder, person, GCP, 200, 900, true);
    seedAbsence(seeder, person, 'Training', 21, 22, 'Congresso de pesquisa clínica.');
  }
}
